import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { v4 } from 'uuid'

import { FormBuilderContent } from './components/form-builder-content/form-builder-content'
import { FormBuilderPreview } from './components/form-builder-preview/form-builder-preview'
import { FormItemDraggableBase } from './components/form-items-draggable/form-item-draggable-base'
import { FormBuilderDroppableId } from './constants/form-builder.constants'
import { FormBuilderContextProvider } from './form-builder.context'
import { FormActiveItem, FormBuilderConfig } from './types/form-builder.types'
import { getIcon } from '../../../assets/icons'
import { useFormBuilderStore } from '../../../entities/form-builder/stores/form-builder.store'

type FormBuilderProps = {
  previewWrapClass?: string
  contentWrapClass?: string
  checklist?: FormBuilderConfig[]
}

export interface FormBuilderHandle {
  getForm: () => FormBuilderConfig[]
  clear: () => void
}

export const FormBuilder = forwardRef<FormBuilderHandle, FormBuilderProps>(
  ({ checklist, previewWrapClass = 'col-6', contentWrapClass = 'col-6' }: FormBuilderProps, ref: ForwardedRef<FormBuilderHandle>) => {
    const [activeItem, setActiveItem] = useState<FormActiveItem | null>(null)
    const { selectedItems, setInitialSelectedItems } = useFormBuilderStore()
    const setSelectedItems = useFormBuilderStore((state) => state.setSelectedItems)
    const clearBuilder = useFormBuilderStore((state) => state.clear)

    const sensors = useSensors(useSensor(PointerSensor))

    useEffect(() => {
      if (checklist) {
        setInitialSelectedItems(checklist)
      }
    }, [checklist])

    const handleDragStart = (event: any) => {
      setActiveItem(event.active.data.current)
    }

    const handleDragEnd = (event: any) => {
      const { over } = event
      if (over?.id === FormBuilderDroppableId && activeItem) {
        setSelectedItems([...selectedItems, { ...activeItem, seq: selectedItems.length }])
      }
    }

    useImperativeHandle(ref, () => ({
      getForm: () =>
        selectedItems.map(
          (item) =>
            ({
              ...item.config,
              id: v4(),
              type: item.type,
              position: item.seq,
            }) as FormBuilderConfig,
        ),
      clear: () => {
        clearBuilder()
      },
    }))

    return (
      <DndContext
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        sensors={sensors}
      >
        <FormBuilderContextProvider>
          <div className={previewWrapClass}>
            <FormBuilderPreview className="h-100" />
          </div>
          <div className={contentWrapClass}>
            <FormBuilderContent className="h-100" />
          </div>

          <DragOverlay>
            {activeItem ? (
              <FormItemDraggableBase
                icon={getIcon(activeItem.icon)}
                id={activeItem.id}
                title={activeItem.title}
                type={activeItem.type}
              />
            ) : null}
          </DragOverlay>
        </FormBuilderContextProvider>
      </DndContext>
    )
  },
)
